import Layout from "../components/site/Layout";
import { PageHeader, SectionLabel, SectionHeading } from "../components/site/Primitives";
import { ArrowUpRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { FIRM } from "../lib/firm";
import { trackEvent } from "../lib/analytics";

const CPA_GAPS = [
  "Proactive multi-year Roth conversion planning",
  "IRMAA Medicare surcharge modeling and mitigation",
  "Widow's tax penalty planning for married couples",
  "IRA inheritance tax planning under the SECURE Act",
  "Advanced conversion structures for large IRA balances",
];

const FA_GAPS = [
  "Tax bracket management across the retirement income window",
  "Legislative risk and tax diversification planning (post-OBBBA)",
  "Coordination of tax, legal, and valuation professionals for advanced strategies",
  "A written, quantified retirement tax strategy plan",
];

const STEPS = [
  {
    n: "STEP 01",
    title: "Introduction",
    body:
      "You make the introduction. We handle the scheduling and the first conversation.",
  },
  {
    n: "STEP 02",
    title: "Risk Analysis",
    body:
      "We conduct a Retirement Tax Risk Analysis and share findings with you — so you are fully informed and positioned as the advisor who surfaced the opportunity.",
  },
  {
    n: "STEP 03",
    title: "Strategy Plan",
    body:
      "If the client proceeds, we deliver a written Retirement Tax Strategy Plan coordinated with your work.",
  },
  {
    n: "STEP 04",
    title: "Implementation",
    body:
      "Implementation is managed by RTC in coordination with you — you stay in the loop on every material decision.",
  },
  {
    n: "STEP 05",
    title: "Documented Relationship",
    body:
      "Revenue sharing is documented in a Professional Adviser Engagement Agreement with a defined structure.",
  },
];

const CLIENT_PROFILE = [
  "$1,000,000 or more in traditional IRA or 401(k) balances",
  "High current income (32% bracket or higher) or projected high income in retirement",
  "Approaching or recently entered retirement — typically ages 55–72",
  "No written retirement tax strategy currently in place",
  "A surviving spouse managing the tax consequences of a partner's passing",
];

const DOCS = [
  {
    tag: "DOC / 01",
    title: "IRA-LLC Strategy Comparison",
    body: "Side-by-side overview of advanced conversion approaches.",
    cta: { label: "Download", to: "/strategy-comparison-gate" },
  },
  {
    tag: "DOC / 02",
    title: "White Paper: Valuation Discounts in IRA-LLC Structures",
    body:
      "Full legal analysis, IRS guidance citations, Tax Court case table, and compliance framework.",
    cta: { label: "Request White Paper", to: "/white-paper-gate" },
  },
  {
    tag: "DOC / 03",
    title: "Professional Adviser Engagement Agreement",
    body: "The governing document for our referral relationship.",
    cta: { label: "Request via email", href: `mailto:${FIRM.email}` },
  },
];

function GapList({ heading, items }) {
  return (
    <div className="bg-cream p-8 lg:p-10">
      <div className="text-[10px] tracking-overline uppercase font-semibold text-accent-gold mb-5">
        {heading}
      </div>
      <ul className="space-y-4">
        {items.map((g, i) => (
          <li key={g} className="flex gap-4">
            <span className="text-[10px] tracking-overline font-semibold text-accent-gold pt-1.5">
              {String(i + 1).padStart(2, "0")}
            </span>
            <span className="text-deep-teal text-sm leading-relaxed">{g}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function GapSection() {
  return (
    <section className="py-24 lg:py-32 bg-cream-alt" data-testid="fa-gap">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionLabel index="01" label="The Gap We Fill" />
        <SectionHeading italicTail="and the financial advisor.">
          We operate in the space between the CPA
        </SectionHeading>
        <p className="mt-6 text-charcoal-teal max-w-3xl leading-relaxed">
          Not to replace either — to do what neither is positioned to do.
          The result is a better outcome for the client and a stronger
          relationship for you.
        </p>
        <div className="mt-12 grid lg:grid-cols-2 gap-px bg-sage/30 border border-sage/30">
          <GapList heading={"What CPAs typically don\u2019t do"} items={CPA_GAPS} />
          <GapList heading={"What financial advisors typically don\u2019t do"} items={FA_GAPS} />
        </div>
      </div>
    </section>
  );
}

function HowWeWorkSection() {
  return (
    <section className="py-24 lg:py-32 bg-cream" data-testid="fa-how">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionLabel index="02" label="How We Work With You" />
        <SectionHeading italicTail="professional referral relationship.">
          A defined,
        </SectionHeading>

        <div className="mt-14 grid lg:grid-cols-5 gap-px bg-sage/30 border border-sage/30">
          {STEPS.map((s) => (
            <div key={s.n} className="bg-cream p-7 lg:p-8 flex flex-col">
              <div className="text-[10px] tracking-overline uppercase font-semibold text-accent-gold">
                {s.n}
              </div>
              <h3 className="mt-5 font-serif text-deep-teal text-xl leading-snug">
                {s.title}
              </h3>
              <p className="mt-3 text-charcoal-teal text-sm leading-relaxed">
                {s.body}
              </p>
            </div>
          ))}
        </div>

        <RevenueShareBlock />
      </div>
    </section>
  );
}

function RevenueShareBlock() {
  return (
    <div className="mt-16 bg-deep-teal text-cream p-10 lg:p-14 grid lg:grid-cols-12 gap-10 items-center">
      <div className="lg:col-span-7">
        <div className="text-[10px] tracking-overline uppercase font-semibold text-accent-gold">
          Revenue Sharing
        </div>
        <h3 className="mt-4 font-serif text-3xl lg:text-4xl leading-snug">
          A defined,{" "}
          <span className="italic text-accent-gold">25% revenue sharing</span>{" "}
          structure for qualified partners.
        </h3>
        <p className="mt-5 text-cream/85 leading-relaxed">
          Terms are documented in a Professional Adviser Engagement
          Agreement. Reach out to request a copy.
        </p>
      </div>
      <div className="lg:col-span-5 lg:pl-8 lg:border-l lg:border-cream/20">
        <div className="text-[10px] tracking-overline uppercase font-semibold text-accent-gold mb-3">
          At a Glance
        </div>
        <div className="font-serif text-6xl lg:text-7xl text-accent-gold">25%</div>
        <p className="mt-3 text-sm text-cream/80">
          Revenue share for qualified professional referral partners.
        </p>
      </div>
    </div>
  );
}

function ClientProfileSection() {
  return (
    <section className="py-24 lg:py-32 bg-cream-alt" data-testid="fa-clients">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionLabel index="03" label="What Your Clients Look Like" />
        <SectionHeading italicTail="share these characteristics.">
          The referrals that fit best
        </SectionHeading>
        <ul className="mt-12 grid sm:grid-cols-2 gap-px bg-sage/30 border border-sage/30">
          {CLIENT_PROFILE.map((c, i) => (
            <li key={c} className="bg-cream p-7 lg:p-8 flex gap-5">
              <span className="text-[10px] tracking-overline uppercase font-semibold text-accent-gold pt-1">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="text-deep-teal text-base leading-relaxed">{c}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function DocCard({ d }) {
  const ctaClass =
    "mt-6 inline-flex items-center gap-2 text-xs tracking-overline uppercase font-semibold text-accent-gold editorial-link";
  return (
    <div className="bg-cream p-7 lg:p-8 flex flex-col">
      <div className="text-[10px] tracking-overline uppercase font-semibold text-accent-gold">
        {d.tag}
      </div>
      <h3 className="mt-5 font-serif text-deep-teal text-xl leading-snug">
        {d.title}
      </h3>
      <p className="mt-3 text-charcoal-teal text-sm leading-relaxed flex-1">
        {d.body}
      </p>
      {d.cta.to ? (
        <Link to={d.cta.to} className={ctaClass}>
          {d.cta.label}
          <ArrowUpRight size={14} weight="light" />
        </Link>
      ) : (
        <a href={d.cta.href} className={ctaClass}>
          {d.cta.label}
          <ArrowUpRight size={14} weight="light" />
        </a>
      )}
    </div>
  );
}

function ResourcesSection() {
  return (
    <section className="py-24 lg:py-32 bg-cream" data-testid="fa-resources">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionLabel index="04" label="Professional Resources" />
        <SectionHeading italicTail="qualified referral partners.">
          Available to
        </SectionHeading>
        <p className="mt-6 text-charcoal-teal max-w-3xl italic leading-relaxed">
          Both documents are shared in confidence as professional resources
          and are not intended for redistribution.
        </p>

        <div className="mt-12 grid lg:grid-cols-3 gap-px bg-sage/30 border border-sage/30">
          {DOCS.map((d) => (
            <DocCard key={d.tag} d={d} />
          ))}
        </div>

        <div className="mt-14 flex flex-wrap items-center gap-6">
          <a
            href={FIRM.calendly15}
            target="_blank"
            rel="noreferrer"
            onClick={() =>
              trackEvent("calendly_click_15min", {
                meta: { source: "for_advisors_primary" },
              })
            }
            data-testid="fa-primary-cta"
            className="group inline-flex items-center gap-3 px-7 py-4 bg-accent-gold !text-white text-xs tracking-overline uppercase font-medium hover:bg-accent-gold-hover transition-all"
          >
            Request a Professional Introduction Call
            <ArrowUpRight
              size={16}
              weight="light"
              className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
            />
          </a>
          <Link
            to="/white-paper-gate"
            className="inline-flex items-center gap-2 text-sm text-deep-teal editorial-link"
          >
            Request the White Paper
            <ArrowUpRight size={16} weight="light" />
          </Link>
          <Link
            to="/strategy-comparison-gate"
            className="inline-flex items-center gap-2 text-sm text-deep-teal editorial-link"
          >
            Download the Strategy Comparison
            <ArrowUpRight size={16} weight="light" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function ForAdvisorsPage() {
  return (
    <Layout pageName="for-advisors">
      <PageHeader
        eyebrow="For CPAs, Financial Advisors & Estate Planning Attorneys"
        title="Your clients have a retirement tax problem. We solve it — and we make"
        italicTail="you look like the one who found it."
        intro="Retirement Tax Consultants is not a competitor. We don't manage assets, file returns, or draft documents. We do one thing: quantify and reduce the retirement tax exposure of affluent clients who have more in traditional IRAs and 401(k)s than is tax-efficient — and we do it in partnership with their existing CPA, financial advisor, and estate planning attorney."
      />
      <GapSection />
      <HowWeWorkSection />
      <ClientProfileSection />
      <ResourcesSection />
    </Layout>
  );
}
